import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { connectCrmSocket } from '../lib/socket';
import { Icon } from '../components/icons';
import { Badge, PageHeader, SectionCard, Spinner, useDebounce } from '../components/ui';
import { fmtMoney, fmtNumber, fmtRelative, timeAgo } from '../lib/format';

interface LiveRoom {
  id: string;
  name: string;
  playerCount: number;
  maxPlayers: number;
  phase: string;
  gameType: string;
  isPrivate: boolean;
  ownerId?: string;
  ownerNickname?: string;
  rakePct: number;
  totalRakeCollected: number;
  buyIn: number;
  bank: number;
  iconId: string;
  createdAt: number;
}

interface LivePlayer {
  id: string;
  nickname: string;
  isConnected: boolean;
}

interface RoomsListItem extends LiveRoom {
  override?: { notes: string | null; tags: string[]; pinned: boolean } | null;
}

export function RoomsPage() {
  const [list, setList] = useState<RoomsListItem[]>([]);
  const [live, setLive] = useState<Map<string, LiveRoom>>(new Map());
  const [players, setPlayers] = useState<Map<string, LivePlayer[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'private'>('all');
  const debouncedSearch = useDebounce(search, 250);
  const navigate = useNavigate();

  useEffect(() => {
    const sock = connectCrmSocket();
    const onHello = () => sock.emit('crm:subscribe');
    const onSnap = (payload: { rooms: LiveRoom[] }) => {
      const m = new Map<string, LiveRoom>();
      for (const r of payload.rooms) m.set(r.id, r);
      setLive(m);
    };
    sock.on('crm:hello', onHello);
    sock.on('crm:rooms:snapshot', onSnap);
    if (sock.connected) sock.emit('crm:subscribe');
    return () => {
      sock.off('crm:hello', onHello);
      sock.off('crm:rooms:snapshot', onSnap);
    };
  }, []);

  // Pull live player list (id, nickname, isConnected) per occupied room
  // from the existing /api/debug/room/:id endpoint. The endpoint already
  // returns `players` for every room in memory; we only need id/nickname/
  // isConnected and ignore `hand` (displayed nowhere — that field is
  // sensitive, peek requires a crm_peek_grants row in the proper flow).
  useEffect(() => {
    let cancelled = false;
    const fetchPlayers = async () => {
      const occupied = list.filter((r) => r.playerCount > 0);
      if (occupied.length === 0) {
        if (!cancelled) setPlayers(new Map());
        return;
      }
      const entries = await Promise.all(
        occupied.map(async (r) => {
          try {
            const data: any = await api.get(`/api/debug/room/${r.id}`);
            if (!data || data.error || !Array.isArray(data.players)) return [r.id, []] as const;
            return [
              r.id,
              data.players.map((p: any) => ({
                id: p.id,
                nickname: p.nickname,
                isConnected: !!p.isConnected
              }))
            ] as const;
          } catch {
            return [r.id, []] as const;
          }
        })
      );
      if (cancelled) return;
      setPlayers(new Map(entries as [string, LivePlayer[]][]));
    };
    fetchPlayers();
    const id = setInterval(fetchPlayers, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [list]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    api.get<{ items: RoomsListItem[] }>(`/api/crm/rooms?${params.toString()}`)
      .then((r) => setList(r.items))
      .finally(() => setLoading(false));
  }, [debouncedSearch]);

  const merged = useMemo(() => {
    return list.map((it) => {
      const liveSnap = live.get(it.id);
      return {
        ...it,
        playerCount: liveSnap?.playerCount ?? it.playerCount,
        phase: liveSnap?.phase ?? 'lobby',
        bank: liveSnap?.bank ?? it.bank,
        ownerNickname: liveSnap?.ownerNickname ?? it.ownerNickname
      };
    });
  }, [list, live]);

  const filtered = useMemo(() => {
    return merged.filter((r) => {
      if (filter === 'private' && !r.isPrivate) return false;
      if (filter === 'active' && r.playerCount === 0) return false;
      return true;
    });
  }, [merged, filter]);

  const totals = useMemo(() => {
    const totalRake = merged.reduce((s, r) => s + Number(r.totalRakeCollected), 0);
    const totalBank = merged.reduce((s, r) => s + Number(r.bank), 0);
    const active = merged.filter((r) => r.playerCount > 0).length;
    return { totalRake, totalBank, active };
  }, [merged]);

  return (
    <div>
      <PageHeader
        title="Комнаты"
        subtitle={`Live · ${filtered.length} показано`}
        actions={
          <>
            <div className="relative">
              <Icon.Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                className="input pl-9 w-56"
                placeholder="Поиск по названию…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select className="input w-44" value={filter} onChange={(e) => setFilter(e.target.value as any)}>
              <option value="all">Все</option>
              <option value="active">С игроками</option>
              <option value="private">Приватные</option>
            </select>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <div className="stat-card">
          <div className="label">Активных комнат</div>
          <div className="value">{fmtNumber(totals.active)}</div>
        </div>
        <div className="stat-card">
          <div className="label">Банк в игре</div>
          <div className="value">{fmtMoney(totals.totalBank)} <span className="text-stone-500 text-sm">фишек</span></div>
        </div>
        <div className="stat-card">
          <div className="label">Рейк заработан</div>
          <div className="value text-emerald-300">{fmtMoney(totals.totalRake)}</div>
        </div>
      </div>

      <SectionCard>
        {loading ? (
          <div className="flex items-center gap-2 text-stone-400 text-sm py-6"><Spinner /> Загрузка…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-stone-400 py-8 text-center">Комнаты не найдены</div>
        ) : (
          <div className="overflow-x-auto -mx-3">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Комната</th>
                  <th>Фаза</th>
                  <th>Игроки</th>
                  <th>Владелец</th>
                  <th>Банк</th>
                  <th>Рейк</th>
                  <th>Создана</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="cursor-pointer" onClick={() => navigate(`/rooms/${r.id}`)}>
                    <td>
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20 flex items-center justify-center">
                          <Icon.Spade size={16} />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-1.5">
                            {r.name}
                            {r.isPrivate && <Icon.Lock size={12} className="text-stone-500" />}
                            {r.override?.pinned && <Icon.Bell size={12} className="text-amber-300" />}
                          </div>
                          <div className="text-[11px] text-stone-500 font-mono">{r.id.slice(0, 8)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <PhaseBadge phase={r.phase} gameType={r.gameType} />
                    </td>
                    <td>
                      <RoomPlayersCell
                        roomId={r.id}
                        count={r.playerCount}
                        max={r.maxPlayers}
                        players={players.get(r.id) ?? []}
                      />
                    </td>
                    <td className="text-stone-300 text-sm">{r.ownerNickname ?? <span className="text-stone-500">—</span>}</td>
                    <td className="font-mono">{fmtMoney(r.bank)}</td>
                    <td className="font-mono text-emerald-300">{fmtMoney(r.totalRakeCollected)}</td>
                    <td className="text-stone-400 text-xs">{fmtRelative(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

function RoomPlayersCell({
  roomId,
  count,
  max,
  players
}: {
  roomId: string;
  count: number;
  max: number;
  players: LivePlayer[];
}) {
  return (
    <div className="flex flex-col gap-1 min-w-[180px]">
      <div className="flex items-center gap-1.5">
        <span className="font-mono">{count}</span>
        <span className="text-stone-500">/</span>
        <span className="text-stone-500">{max}</span>
        {count > 0 && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
      </div>
      {count > 0 && (
        <div className="flex flex-wrap gap-1">
          {players.length === 0 ? (
            <span className="text-[11px] text-stone-500">…</span>
          ) : (
            players.map((p) => (
              <span
                key={p.id}
                title={p.isConnected ? 'онлайн' : 'AFK / disconnected'}
                className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${
                  p.isConnected
                    ? 'bg-emerald-400/10 text-emerald-300 ring-1 ring-emerald-400/20'
                    : 'bg-stone-500/10 text-stone-400 ring-1 ring-stone-500/20'
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    p.isConnected ? 'bg-emerald-400' : 'bg-stone-500'
                  }`}
                />
                {p.nickname}
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function PhaseBadge({ phase, gameType }: { phase: string; gameType: string }) {
  const map: Record<string, { tone: 'amber' | 'green' | 'red' | 'sky' | 'stone'; label: string }> = {
    lobby: { tone: 'stone', label: 'Лобби' },
    combos: { tone: 'sky', label: 'Комбо' },
    playing: { tone: 'green', label: 'Игра' },
    round_end: { tone: 'amber', label: 'Раунд окончен' },
    game_over: { tone: 'red', label: 'Партия окончена' },
    attacking: { tone: 'green', label: 'Атака' },
    defending: { tone: 'amber', label: 'Защита' },
    resolve: { tone: 'amber', label: 'Розыгрыш' },
    draw: { tone: 'sky', label: 'Добор' },
    finished: { tone: 'red', label: 'Финиш' }
  };
  const v = map[phase] ?? { tone: 'stone' as const, label: phase };
  return <Badge tone={v.tone}>{v.label}</Badge>;
}
